import { Grid, Select, Stack } from '@mantine/core';
import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Form, FormAnalyticsAggregateResponse, FormAnalyticsResponse, Message } from '@/api';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { usePagination } from '@/hooks/usePagination';
import { PageHeader } from '../../../components/Common/PageHeader';
import {
  KpiStrip,
  PillStats,
  TopCountriesList,
  VolumeChart,
} from '../../../components/Message/dashboard';
import { MessagesTable } from '../../../components/Message/Table';
import { useRustyState } from '../../../state';

export interface AccountFormsStartPageProps {
  formId?: string;
  showTableInfo?: boolean;
}

export function AccountMessagesStartPage(props: AccountFormsStartPageProps) {
  const { t } = useTranslation();
  const { api } = useRustyState.getState();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();

  const [analyticsResponse, setAnalyticsResponse] = useState<FormAnalyticsResponse | null>(null);
  const [aggregateAnalytics, setAggregateAnalytics] =
    useState<FormAnalyticsAggregateResponse | null>(null);
  const [loadingAggregateAnalytics, setLoadingAggregateAnalytics] = useState(false);

  const [perPage, setPerPage] = useState(() => {
    if (typeof window !== 'undefined' && window.localStorage) {
      const saved = localStorage.getItem('formshive.messages.pagination');
      return saved ? parseInt(saved, 10) : 10;
    }
    return 10;
  });

  const perPageRef = useRef(perPage);

  // Track last filter value to avoid refetching analytics on pagination
  const lastAnalyticsFilterRef = useRef<boolean | undefined | 'initial'>('initial');

  const getAnalytics = async (params?: { is_spam?: boolean }) => {
    const formId = props.formId ? [props.formId] : undefined;
    const countParams: {
      form_ids?: string[];
      is_spam?: boolean;
      data_type?: 'views' | 'messages' | 'combined';
    } = {
      data_type: 'combined',
    };
    if (formId) {
      countParams.form_ids = formId;
    }
    if (params && 'is_spam' in params && params.is_spam !== undefined) {
      countParams.is_spam = params.is_spam;
    }

    const res = (await api.getMessageCountByDay(countParams)) as unknown as FormAnalyticsResponse;
    setAnalyticsResponse(res);
  };

  const getAggregateAnalytics = async (params?: { is_spam?: boolean }) => {
    try {
      setLoadingAggregateAnalytics(true);
      const aggregateParams: {
        form_ids?: string[];
        is_spam?: boolean;
      } = {};

      if (props.formId) {
        aggregateParams.form_ids = [props.formId];
      }
      if (params && 'is_spam' in params && params.is_spam !== undefined) {
        aggregateParams.is_spam = params.is_spam;
      }

      const res = await api.getAnalyticsAggregate(aggregateParams);
      setAggregateAnalytics(res);
    } finally {
      setLoadingAggregateAnalytics(false);
    }
  };

  const fetchMessages = async (params: { nextPage: number; [key: string]: any }) => {
    const { nextPage, ...otherParams } = params;
    const currentPerPage = perPageRef.current;
    const apiParams: any = {
      offset: nextPage === 1 ? 0 : currentPerPage * (nextPage - 1),
      limit: currentPerPage,
      ...otherParams,
    };

    if (props.formId) {
      apiParams.form_id = props.formId;
    }

    // Only refetch analytics when filter changes (not on pagination)
    const filterChanged = lastAnalyticsFilterRef.current !== params.is_spam;
    const analyticsPromises: Promise<void>[] = [];

    if (filterChanged) {
      analyticsPromises.push(getAnalytics({ is_spam: params.is_spam }));
      analyticsPromises.push(getAggregateAnalytics({ is_spam: params.is_spam }));
      lastAnalyticsFilterRef.current = params.is_spam;
    }

    // Get messages data and run analytics in parallel (if needed)
    const [messagesResult] = await Promise.all([
      useRustyState.getState().getMessagesWithForms(apiParams),
      ...analyticsPromises,
    ]);

    return {
      data: messagesResult.data,
      total: messagesResult.total,
    };
  };

  const pagination = usePagination({
    perPage,
    fetchData: fetchMessages,
  });

  const openForm = (data: { msg: Message; form: Form | undefined }) => {
    nav(createLanguageURL(`/account/message/${data.msg.id}`));
  };

  const handleMessagesChange = async (params: { nextPage: number; [key: string]: any }) => {
    return await pagination.loadPage(params.nextPage, params);
  };

  const handlePerPageChange = (newPerPage: number) => {
    setPerPage(newPerPage);
    perPageRef.current = newPerPage; // Update ref immediately
    if (typeof window !== 'undefined' && window.localStorage) {
      localStorage.setItem('formshive.messages.pagination', newPerPage.toString());
    }
  };

  const paginationOptions = [
    { value: '10', label: '10' },
    { value: '20', label: '20' },
    { value: '30', label: '30' },
    { value: '40', label: '40' },
    { value: '50', label: '50' },
  ];

  // Compute trend ("this week vs prior week") from the per-day series.
  const trends = useMemo(() => {
    const series = analyticsResponse?.data ?? [];
    if (series.length < 14) {
      return {
        viewsTrendPct: null,
        messagesTrendPct: null,
        conversionTrendPct: null,
      };
    }
    const tail = series.slice(-14);
    const prior = tail.slice(0, 7);
    const recent = tail.slice(7);
    const sum = (arr: typeof series, key: 'view_count' | 'message_count') =>
      arr.reduce((s, d) => s + (d[key] ?? 0), 0);
    const pct = (cur: number, prev: number): number | null => {
      if (prev === 0) {
        return cur === 0 ? 0 : null;
      }
      return ((cur - prev) / prev) * 100;
    };
    const viewsCur = sum(recent, 'view_count');
    const viewsPrev = sum(prior, 'view_count');
    const msgCur = sum(recent, 'message_count');
    const msgPrev = sum(prior, 'message_count');
    const convCur = viewsCur > 0 ? (msgCur / viewsCur) * 100 : null;
    const convPrev = viewsPrev > 0 ? (msgPrev / viewsPrev) * 100 : null;
    return {
      viewsTrendPct: pct(viewsCur, viewsPrev),
      messagesTrendPct: pct(msgCur, msgPrev),
      conversionTrendPct: convCur === null || convPrev === null ? null : pct(convCur, convPrev),
    };
  }, [analyticsResponse]);

  const totalViews = analyticsResponse?.view_count ?? 0;
  const totalMessages = analyticsResponse?.message_count ?? 0;
  const conversionRate = totalViews > 0 ? Math.min((totalMessages / totalViews) * 100, 100) : null;

  return (
    <>
      <PageHeader
        title={t('accountPages.messagesChartLabel')}
        subtitle={t('accountPages.messagesDescription')}
      />
      <Stack gap="xl">
        <KpiStrip
          totalViews={totalViews}
          totalMessages={totalMessages}
          conversionRate={conversionRate}
          messagesTrendPct={trends.messagesTrendPct}
          viewsTrendPct={trends.viewsTrendPct}
          conversionTrendPct={trends.conversionTrendPct}
        />

        <PillStats aggregate={aggregateAnalytics} loading={loadingAggregateAnalytics} />

        <Grid>
          <Grid.Col span={{ base: 12, lg: 4 }}>
            <TopCountriesList aggregate={aggregateAnalytics} loading={loadingAggregateAnalytics} />
          </Grid.Col>
          <Grid.Col span={{ base: 12, lg: 8 }}>
            <VolumeChart
              data={analyticsResponse?.data ?? []}
              loading={analyticsResponse === null}
            />
          </Grid.Col>
        </Grid>

        <MessagesTable
          pagination={pagination.paginationConfig}
          onChange={handleMessagesChange}
          openRowPage={openForm}
          updateCb={api.updateMessage}
          deleteCb={api.deleteMessage}
          perPage={perPage}
          onPerPageChange={handlePerPageChange}
          headerActions={
            <Select
              data={paginationOptions}
              value={perPage.toString()}
              onChange={(value) => {
                const newPerPage = parseInt(value || '10', 10);
                handlePerPageChange(newPerPage);
              }}
              size="sm"
              w={80}
            />
          }
        />
      </Stack>
    </>
  );
}
